import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colorTokens, typographyTokens } from '../../app/designTokens';
import { RegisterSectionCard } from './RegisterSectionCard';
import { REGISTER_LABEL_SX, REGISTER_VALUE_SX, formatRegisterAmount } from './registerSurfaces';

const { semantic } = colorTokens;

function SummaryRow({ label, value, testId }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
      <Typography sx={REGISTER_LABEL_SX}>{label}</Typography>
      <Typography sx={{ ...REGISTER_VALUE_SX, textAlign: 'right' }} data-testid={testId}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Figma Composite: TrialSummaryCard (steps 1–3 right rail, step 4 final summary).
 * All monetary values come from the backend pricing preview — never computed here.
 */
export function TrialSummaryCard({
  preview,
  loading = false,
  error = '',
  selectedModules = [],
  billingInterval = 'monthly',
  usersCount = 10,
  smsNotificationsEnabled = false,
  startMode = 'trial',
  variant = 'rail',
  testId = 'register-trial-summary',
}) {
  const { t } = useTranslation(['onboarding', 'pricing', 'register', 'common']);

  const currency = preview?.currency || 'EUR';
  const moduleCodes = preview?.selected_module_codes?.length
    ? preview.selected_module_codes
    : selectedModules;
  const annualMonthsCharged = preview?.annual_months_charged;
  const trialDays = preview?.trial_days;

  return (
    <RegisterSectionCard
      title={t('onboarding:register.wizard.summary_title')}
      testId={testId}
      sx={variant === 'rail' ? { position: { md: 'sticky' }, top: { md: 16 } } : undefined}
    >
      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {loading && !preview ? (
        <Typography sx={{ fontSize: 13, color: semantic.text.muted }}>
          {t('common:loading')}
        </Typography>
      ) : null}

      <Stack spacing={1}>
        <SummaryRow
          label={t('onboarding:register.wizard.summary_modules')}
          value={moduleCodes.length > 0
            ? moduleCodes.join(', ')
            : t('onboarding:register.wizard.summary_none')}
          testId={`${testId}-modules`}
        />
        <SummaryRow
          label={t('onboarding:register.wizard.summary_users')}
          value={preview?.user_tier_label || usersCount}
          testId={`${testId}-users`}
        />
        <SummaryRow
          label={t('onboarding:register.wizard.summary_billing')}
          value={billingInterval === 'yearly'
            ? t('pricing:pricing_preview.interval_yearly')
            : t('pricing:pricing_preview.interval_monthly')}
          testId={`${testId}-billing`}
        />
        <SummaryRow
          label={t('onboarding:register.wizard.summary_addons')}
          value={smsNotificationsEnabled
            ? t('register:smsNotifications')
            : t('onboarding:register.wizard.summary_none')}
          testId={`${testId}-addons`}
        />
        <SummaryRow
          label={t('register:startMode')}
          value={startMode === 'buy_now' ? t('register:buyNow') : t('register:startTrial')}
        />
        {trialDays ? (
          <SummaryRow
            label={t('onboarding:register.wizard.summary_trial_days')}
            value={trialDays}
          />
        ) : null}
      </Stack>

      <Divider sx={{ my: 1.5, borderColor: semantic.border.subtle }} />

      <Stack spacing={1}>
        <SummaryRow
          label={t('pricing:register.monthly_total')}
          value={formatRegisterAmount(preview?.monthly_total, currency, t)}
        />
        <SummaryRow
          label={t('pricing:register.yearly_total')}
          value={formatRegisterAmount(preview?.yearly_total, currency, t)}
        />
        {preview?.annual_discount_amount > 0 ? (
          <SummaryRow
            label={t('pricing:register.annual_discount', { months: annualMonthsCharged ?? 11 })}
            value={formatRegisterAmount(preview.annual_discount_amount, currency, t)}
          />
        ) : null}
      </Stack>

      <Box
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: `1px solid ${semantic.border.subtle}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
          <Typography
            sx={{
              fontFamily: typographyTokens.fontFamilyDisplay,
              fontWeight: typographyTokens.fontWeightSemibold,
              fontSize: 14,
              color: semantic.text.primary,
            }}
          >
            {t('pricing:register.amountDue')}
          </Typography>
          <Typography
            data-testid={`${testId}-amount-due`}
            sx={{
              fontFamily: typographyTokens.fontFamilyDisplay,
              fontWeight: typographyTokens.fontWeightBold,
              fontSize: 18,
              color: semantic.text.primary,
            }}
          >
            {formatRegisterAmount(
              moduleCodes.length > 0 ? preview?.amount_due_for_selected_interval : 0,
              currency,
              t,
            )}
          </Typography>
        </Stack>
      </Box>

      {smsNotificationsEnabled ? (
        <Typography sx={{ fontSize: 12, color: semantic.text.muted, mt: 1 }}>
          {t('register:smsNotIncludedInModulePrice')}
        </Typography>
      ) : null}
    </RegisterSectionCard>
  );
}

export default TrialSummaryCard;
